import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Thumbs, FreeMode } from 'swiper/modules';

// Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';
import 'swiper/css/free-mode';

// Icons
import {
    Bath,
    BedDouble,
    Coffee,
    Lamp,
    Phone,
    ShieldCheck,
    Sparkles,
    SunMedium,
    Trees,
    UtensilsCrossed,
    Waves,
    Wifi,
    Wind,
    Droplets
} from 'lucide-react';

// Images
import heroImg from '../assets/River/19.jpeg';
import galleryOne from '../assets/River/44.webp';
import galleryTwo from '../assets/River/20.jpeg';
import galleryThree from '../assets/River/22.jpeg';
import galleryFour from '../assets/River/25.jpeg';
import gallery2 from '../assets/River/11.jpeg';

const highlights = [
    { titleKey: 'highlight1Title', descKey: 'highlight1Desc', icon: Waves },
    { titleKey: 'highlight2Title', descKey: 'highlight2Desc', icon: Lamp },
    { titleKey: 'highlight3Title', descKey: 'highlight3Desc', icon: BedDouble }
];

const inclusions = [
    'inclusion1',
    'inclusion2',
    'inclusion3',
    'inclusion4',
    'inclusion5',
    'inclusion6'
];

const amenityGroups = [
    {
        title: 'Room Includes',
        items: [
            'Free Wifi',
            'Air Conditioning',
            'Hot water',
            'Mini fridge',
            'Safety deposit box',
            'Electric kettle',
            'Daily housekeeping',
            'Hair dryer'
        ]
    }
];

const stats = [
    { labelKey: 'statSizeLabel', value: '55 sqm', icon: Sparkles },
    { labelKey: 'statViewLabel', valueKey: 'statViewValue', icon: SunMedium },
    { labelKey: 'statGuestsLabel', value: '2 Adults', icon: Trees },
    { labelKey: 'statBedLabel', valueKey: 'statBedValue', icon: BedDouble }
];

const galleryImages = [galleryOne, galleryTwo, galleryThree, galleryFour];

export default function DeluxeRoom2() {
    const { t } = useTranslation('deluxerooms2');
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const navigationButtons = [
        { labelKey: 'navAll', path: '/river/rooms' },
        { labelKey: 'navDeluxe', path: '/river/rooms/deluxe-room' },
        { labelKey: 'navRiverViewDeluxe', path: '/river/rooms/deluxe-river-view-room' }
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <motion.div
            className="bg-white text-gray-900"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
        >
            {/* Hero Section */}
            <section className="relative h-[75vh] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${heroImg})` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/20" />
                </div>

                <div className="relative h-full container px-6 flex flex-col justify-end pb-16">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-sm uppercase tracking-[0.3em] text-amber-200 mb-4"
                    >
                        {t('resortName')}
                    </motion.p>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-5xl lg:text-6xl font-light text-white leading-tight max-w-3xl"
                    >
                        {t('heroTitle')}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="mt-6 text-lg md:text-xl text-green-50 max-w-2xl"
                    >
                        {t('heroSubtitle')}
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.35 }}
                        className="mt-8 flex flex-wrap gap-4"
                    >
                        <button
                            onClick={() => navigate('/river/search')}
                            className="px-7 py-3 rounded-full bg-amber-500 text-white font-semibold shadow-lg hover:bg-amber-600 transition-colors"
                        >
                            {t('bookThisRoom')}
                        </button>
                        <a
                            href="https://wa.me/94777382466"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-7 py-3 rounded-full border border-white text-white font-semibold hover:bg-white/10 transition-colors inline-block"
                        >
                            {t('callReservations')}
                        </a>
                    </motion.div>
                </div>
            </section>

            {/* Navigation bar */}
            <section className="sticky top-0 bg-white/95 backdrop-blur-sm shadow-sm z-10 border-b border-green-100">
                <div className="container px-6 py-4">
                    <div className="flex items-center justify-center md:justify-start">
                        <div className="flex space-x-3 overflow-x-auto">
                            {navigationButtons.map((btn) => (
                                <Link
                                    key={btn.path}
                                    to={btn.path}
                                    className={`px-6 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${isActive(btn.path)
                                        ? 'bg-green-700 text-white'
                                        : 'text-gray-600 hover:bg-green-100 hover:text-green-800'
                                        }`}
                                >
                                    {t(btn.labelKey)}
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Main content */}
            <section className="px-6 py-16 space-y-10 bg-[#1F1F1F]">
                <div className="container grid lg:grid-cols-3 gap-10 items-start">
                    <div className="lg:col-span-2 space-y-6">
                        <h2 className="text-3xl md:text-4xl font-light text-white">{t('soothingHideawayTitle')}</h2>
                        <p className="text-lg text-white leading-relaxed">
                            {t('soothingHideawayDesc')}
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                            {highlights.map((item, idx) => (
                                <div key={idx} className="flex items-start gap-3 p-4 rounded-xl bg-emerald-900/30">
                                    <item.icon className="w-6 h-6 text-emerald-400" />
                                    <div>
                                        <p className="font-semibold text-white">{t(item.titleKey)}</p>
                                        <p className="text-sm text-gray-300 leading-relaxed">{t(item.descKey)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-gray-900 text-white rounded-2xl p-8 space-y-6">
                        <p className="text-sm uppercase tracking-[0.2em] text-amber-300">{t('atAGlance')}</p>
                        <div className="grid grid-cols-2 gap-4">
                            {stats.map((stat, idx) => (
                                <div key={idx} className="p-4 rounded-xl bg-white/5 border border-white/10">
                                    <stat.icon className="w-6 h-6 text-amber-300 mb-2" />
                                    <p className="text-sm text-white/70">{t(stat.labelKey)}</p>
                                    <p className="text-lg font-semibold">
                                        {stat.valueKey ? t(stat.valueKey) : stat.value}
                                    </p>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center justify-between">
                            <button
                                onClick={() => navigate('/river/search')}
                                className="px-5 py-3 rounded-full bg-amber-500 text-gray-900 font-semibold hover:bg-amber-400 transition-colors"
                            >
                                {t('reserve')}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Gallery Swiper */}
                <div className="max-w-7xl mx-auto">
                    <Swiper
                        modules={[Navigation, Pagination, Thumbs, FreeMode]}
                        spaceBetween={12}
                        navigation
                        pagination={{ clickable: true }}
                        thumbs={{ swiper: thumbsSwiper }}
                        className="rounded-2xl overflow-hidden mb-4 main-swiper"
                        loop={true}
                    >
                        {galleryImages.map((img, index) => (
                            <SwiperSlide key={index}>
                                <div className="relative aspect-[4/3] md:aspect-[16/9] overflow-hidden">
                                    <img
                                        src={img}
                                        alt={t(`galleryAlt${index + 1}`)}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    <Swiper
                        onSwiper={setThumbsSwiper}
                        modules={[FreeMode, Thumbs]}
                        spaceBetween={10}
                        slidesPerView={4}
                        freeMode={true}
                        watchSlidesProgress={true}
                        className="thumbs-swiper"
                        breakpoints={{
                            640: { slidesPerView: 5 },
                            1024: { slidesPerView: 6 }
                        }}
                    >
                        {galleryImages.map((img, index) => (
                            <SwiperSlide key={index}>
                                <img
                                    src={img}
                                    alt={t(`galleryThumbAlt${index + 1}`)}
                                    className="w-full h-24 object-cover rounded-lg opacity-70 hover:opacity-100 transition-opacity"
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </section>

            {/* Inclusions & Amenities */}
            <section className="bg-gray-50 py-16">
                <div className="max-w-6xl mx-auto px-6 space-y-10">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <p className="text-sm uppercase tracking-[0.2em] text-emerald-700">
                                {t('includedWithStay')}
                            </p>
                            <h3 className="text-3xl font-light text-gray-900">
                                {t('thoughtfulTouches')}
                            </h3>
                        </div>
                        <div className="flex items-center gap-3 text-emerald-700">
                            <ShieldCheck className="w-5 h-5" />
                            <span className="text-sm">{t('enhancedCleanliness')}</span>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="p-6 rounded-2xl bg-white shadow-sm space-y-3">
                            {inclusions.map((key, idx) => (
                                <div key={idx} className="flex items-center gap-3">
                                    <Sparkles className="w-5 h-5 text-amber-500" />
                                    <p className="text-gray-800">{t(key)}</p>
                                </div>
                            ))}
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            {amenityGroups.map((group, idx) => (
                                <div key={idx} className="p-5 rounded-2xl bg-white shadow-sm">
                                    <p className="font-semibold text-gray-900 mb-3">{group.title}</p>
                                    <ul className="space-y-2 text-sm text-gray-700 list-disc list-inside">
                                        {group.items.map((item, i) => (
                                            <li key={i}>{item}</li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* In-room essentials */}
            <section className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-10 items-center">
                <div className="space-y-5">
                    <p className="text-sm uppercase tracking-[0.2em] text-emerald-700">Room Includes</p>
                    <h3 className="text-3xl font-light text-gray-900">Everything you need</h3>
                    <p className="text-lg text-gray-700 leading-relaxed">Basic amenities included in every room</p>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {[
                            { icon: Wifi, label: 'Free Wifi' },
                            { icon: Wind, label: 'Air Conditioning' },
                            { icon: Droplets, label: 'Hot water' },
                            { icon: Coffee, label: 'Mini fridge' },
                            { icon: ShieldCheck, label: 'Safety deposit box' },
                            { icon: UtensilsCrossed, label: 'Electric kettle' },
                            { icon: Sparkles, label: 'Daily housekeeping' },
                            { icon: Bath, label: 'Hair dryer' }
                        ].map((item, idx) => (
                            <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50">
                                <item.icon className="w-5 h-5 text-emerald-700" />
                                <p className="text-sm font-medium text-gray-900">{item.label}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="relative h-80 md:h-96 rounded-3xl overflow-hidden">
                    <img src={gallery2} alt={t('gallery2Alt')} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    <div className="absolute bottom-6 left-6 text-white">
                        <p className="text-sm uppercase tracking-[0.2em] text-amber-200">{t('byTheRiver')}</p>
                        <p className="text-2xl font-semibold">{t('slowDownBreathe')}</p>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-gray-900 text-white py-14">
                <div className="max-w-6xl mx-auto px-6 flex flex-col lg:flex-row items-center justify-between gap-6">
                    <div>
                        <p className="text-sm uppercase tracking-[0.2em] text-amber-300">{t('readyToStay')}</p>
                        <h4 className="text-3xl font-light">{t('reserveDeluxeToday')}</h4>
                        <p className="text-white/70 mt-2">{t('speakReservations')}</p>
                    </div>
                    <div className="flex flex-wrap gap-4">
                        <button
                            onClick={() => navigate('/river/search')}
                            className="px-6 py-3 rounded-full bg-amber-500 text-gray-900 font-semibold hover:bg-amber-400 transition-colors"
                        >
                            {t('checkAvailability')}
                        </button>
                        <a
                            href="https://wa.me/94777382466"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-6 py-3 rounded-full border border-white text-white font-semibold hover:bg-white/10 transition-colors flex items-center gap-2"
                        >
                            <Phone className="w-5 h-5" />
                            +94 77 738 2466
                        </a>
                    </div>
                </div>
            </section>
        </motion.div>
    );
}